import type { IconName } from '../components/icons';
import type { StatusTone } from '../components/ui/StatusBadge';
import type { PlanLinkedInPost, PlanMediaAsset, PlanPostIdea } from '../services/api';

/**
 * Stand und Sprungziele einer Beitragsidee -- an einer Stelle, damit
 * Wochenplan, Ideenliste und alle anderen Seiten dieselbe Sprache sprechen.
 *
 * Die Reihenfolge der Stufen ist die des LinkedIn-Ablaufs:
 * Idee -> Brief -> Text -> Termin -> Live.
 */

export type IdeaStageKey = 'empty' | 'idea' | 'brief' | 'text' | 'scheduled' | 'live';

export interface IdeaStage {
  key: IdeaStageKey;
  /** 0 = leer, 1..5 = erreichte Stufe im Ablauf. */
  step: number;
  label: string;
  tone: StatusTone;
  /** Was als Naechstes zu tun ist (Tooltip, Hinweistext). */
  next: string;
}

export const STAGE_STEPS: { key: Exclude<IdeaStageKey, 'empty'>; label: string }[] = [
  { key: 'idea', label: 'Idee' },
  { key: 'brief', label: 'Brief' },
  { key: 'text', label: 'Text' },
  { key: 'scheduled', label: 'Termin' },
  { key: 'live', label: 'Live' },
];

const STAGES: Record<IdeaStageKey, IdeaStage> = {
  empty: { key: 'empty', step: 0, label: 'Ohne Beitrag', tone: 'danger', next: 'Diesem Slot eine Beitragsidee zuweisen.' },
  idea: { key: 'idea', step: 1, label: 'Nur Idee', tone: 'neutral', next: 'Brief schreiben (Thema, Herausforderung, Learning).' },
  brief: { key: 'brief', step: 2, label: 'Brief fertig', tone: 'info', next: 'Aus dem Brief den LinkedIn-Text erstellen.' },
  text: { key: 'text', step: 3, label: 'Text fertig', tone: 'primary', next: 'Version auswählen und terminieren.' },
  scheduled: { key: 'scheduled', step: 4, label: 'Terminiert', tone: 'warning', next: 'Freigeben bzw. Versand abwarten.' },
  live: { key: 'live', step: 5, label: 'Veröffentlicht', tone: 'success', next: 'Kennzahlen unter „Messen“ ansehen.' },
};

const LIVE = new Set(['published', 'posted']);

/** Die gewaehlte Version, sonst die erste. */
export function mainPost(idea: PlanPostIdea | null | undefined): PlanLinkedInPost | undefined {
  const posts = idea?.linkedInPosts ?? [];
  return posts.find((p) => p.isSelected) ?? posts[0];
}

/** Erster Termin (Kalender vor Freigabe-Warteschlange). */
export function mainSchedule(idea: PlanPostIdea | null | undefined) {
  const entry = (idea?.linkedInPosts ?? []).map((p) => p.publicationEntry).find(Boolean);
  if (entry) {
    return { kind: 'calendar' as const, id: entry.id, status: entry.status, date: entry.scheduledDate, url: entry.linkedInPostUrl ?? undefined };
  }
  const sp = (idea?.scheduledPosts ?? [])[0];
  if (sp) {
    return { kind: 'queue' as const, id: sp.id, status: sp.status, date: sp.scheduledDate, url: sp.linkedInPostUrl ?? undefined };
  }
  return undefined;
}

export function ideaStage(idea: PlanPostIdea | null | undefined): IdeaStage {
  if (!idea) return STAGES.empty;
  const schedule = mainSchedule(idea);
  const anyLive = (idea.linkedInPosts ?? []).some((p) => p.publicationEntry && LIVE.has(p.publicationEntry.status))
    || (idea.scheduledPosts ?? []).some((s) => LIVE.has(s.status));
  if (anyLive || idea.status === 'posted') return STAGES.live;
  if (schedule) return STAGES.scheduled;
  if ((idea.linkedInPosts ?? []).length > 0) return STAGES.text;
  if (idea.contentBrief) return STAGES.brief;
  return STAGES.idea;
}

export const STAGE_ORDER: IdeaStageKey[] = ['empty', 'idea', 'brief', 'text', 'scheduled', 'live'];
export const stageByKey = (key: IdeaStageKey): IdeaStage => STAGES[key];

const FORMAT_BY_KIND: Record<PlanMediaAsset['kind'], { label: string; icon: IconName }> = {
  video: { label: 'Video', icon: 'play' },
  pdf: { label: 'Karussell', icon: 'layers' },
  image: { label: 'Grafik', icon: 'image' },
  document: { label: 'Dokument', icon: 'file-text' },
};

/**
 * Beitragsformat aus den Medien: Video schlaegt Karussell schlaegt Grafik.
 * Ohne Medien ist es ein Textbeitrag.
 */
export function ideaFormat(idea: PlanPostIdea | null | undefined): { label: string; icon: IconName } {
  const kinds = new Set((idea?.mediaAssets ?? []).map((m) => m.kind));
  for (const k of ['video', 'pdf', 'image', 'document'] as const) {
    if (kinds.has(k)) return FORMAT_BY_KIND[k];
  }
  return { label: 'Text', icon: 'file-text' };
}

const PROFILE_LABEL: Record<string, string> = { david: 'David', florian: 'Florian', neutral: 'Unternehmensseite' };

/** Wer postet: Profil der Medien, sonst unbekannt. */
export function ideaVoice(idea: PlanPostIdea | null | undefined): string | undefined {
  const profile = (idea?.mediaAssets ?? []).map((m) => m.profile).find(Boolean);
  return profile ? PROFILE_LABEL[profile] ?? profile : undefined;
}

/** Ein Satz, der zeigt, worum es geht: Hook, sonst Brief-Thema, sonst Beschreibung. */
export function ideaTeaser(idea: PlanPostIdea | null | undefined, max = 180): string | undefined {
  const raw = mainPost(idea)?.hook || idea?.contentBrief?.theme || idea?.description || idea?.painPoint;
  if (!raw) return undefined;
  const flat = raw.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

export interface RelatedLink {
  key: string;
  label: string;
  icon: IconName;
  /** Interner Pfad (react-router) ... */
  to?: string;
  /** ... oder externe Adresse (neuer Tab). */
  href?: string;
  /** Erreicht (gefuellt) oder noch offen (gestrichelt, fuehrt zum Anlegen). */
  done: boolean;
  title: string;
}

const enc = encodeURIComponent;
const kurz = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

/** Alle Sprungziele einer Idee in Ablauf-Reihenfolge. */
export function ideaLinks(idea: PlanPostIdea): RelatedLink[] {
  const id = enc(idea.id);
  const post = mainPost(idea);
  const postCount = idea.linkedInPosts?.length ?? 0;
  const mediaCount = idea.mediaAssets?.length ?? 0;
  const schedule = mainSchedule(idea);
  const links: RelatedLink[] = [
    { key: 'idea', label: 'Idee', icon: 'lightbulb', to: `/post-ideas?id=${id}`, done: true, title: `Beitragsidee „${idea.title}“ öffnen` },
    {
      key: 'brief', label: 'Brief', icon: 'file-text', to: `/content-briefs?postIdeaId=${id}`, done: Boolean(idea.contentBrief),
      title: idea.contentBrief ? `Brief: ${idea.contentBrief.theme}` : 'Noch kein Brief – jetzt anlegen',
    },
    {
      key: 'text',
      label: postCount > 1 ? `Text (${postCount} Versionen)` : 'Text',
      icon: 'edit',
      to: `/linkedin-post-generator?postIdeaId=${id}${post ? `&postId=${enc(post.id)}` : ''}`,
      done: postCount > 0,
      title: post ? `Version ${post.version}${post.isSelected ? ' (ausgewählt)' : ''} öffnen` : 'Noch kein Text – aus dem Brief erstellen',
    },
    {
      key: 'media', label: mediaCount > 0 ? `Medien (${mediaCount})` : 'Medien', icon: 'image', to: `/media?postIdeaId=${id}`,
      done: mediaCount > 0, title: mediaCount > 0 ? 'Videos, Karussells und Bilder dieser Idee' : 'Noch keine Medien verknüpft',
    },
  ];
  if (schedule) {
    links.push({
      key: 'schedule',
      label: `Termin ${kurz(schedule.date)}`,
      icon: 'clock',
      to: schedule.kind === 'calendar' ? `/publication-calendar?entry=${enc(schedule.id)}` : `/scheduled-posts?id=${enc(schedule.id)}`,
      done: true,
      title: schedule.kind === 'calendar' ? 'Im Veröffentlichungskalender öffnen' : 'In „Freigabe & Versand“ öffnen',
    });
  } else if (post) {
    links.push({
      key: 'schedule', label: 'Terminieren', icon: 'clock', to: `/linkedin-post-generator?postIdeaId=${id}&postId=${enc(post.id)}`,
      done: false, title: 'Text ist fertig, aber noch nicht terminiert',
    });
  }
  if (schedule?.url) {
    links.push({ key: 'live', label: 'Auf LinkedIn', icon: 'external-link', href: schedule.url, done: true, title: 'Veröffentlichten Beitrag auf LinkedIn öffnen' });
  }
  return links;
}

export type BacklinkKey = 'idea' | 'brief' | 'text' | 'media' | 'flyer' | 'plan';

/**
 * Rueckwege von jeder Seite, die nur die Idee kennt (Kalender, Freigabe,
 * Medien, Brief, Generator). `omit` laesst die Seite weg, auf der man steht.
 */
export function ideaBacklinks(
  postIdeaId: string,
  opts: {
    postId?: string;
    hasBrief?: boolean;
    /** Bekannte Zaehler (Ideenliste); ohne Angabe gilt der Schritt als vorhanden. */
    postCount?: number;
    mediaCount?: number;
    flyerCount?: number;
    omit?: BacklinkKey[];
  } = {},
): RelatedLink[] {
  const id = enc(postIdeaId);
  const links: (RelatedLink & { key: BacklinkKey })[] = [
    { key: 'idea', label: 'Idee', icon: 'lightbulb', to: `/post-ideas?id=${id}`, done: true, title: 'Beitragsidee öffnen' },
    {
      key: 'brief', label: 'Brief', icon: 'file-text', to: `/content-briefs?postIdeaId=${id}`, done: opts.hasBrief !== false,
      title: opts.hasBrief === false ? 'Noch kein Brief – jetzt anlegen' : 'Brief öffnen',
    },
    {
      key: 'text',
      label: opts.postCount && opts.postCount > 1 ? `Text (${opts.postCount} Versionen)` : 'Text',
      icon: 'edit',
      done: opts.postCount !== 0,
      title: opts.postCount === 0 ? 'Noch kein Text – aus dem Brief erstellen' : 'Textversionen öffnen',
      to: `/linkedin-post-generator?postIdeaId=${id}${opts.postId ? `&postId=${enc(opts.postId)}` : ''}`,
    },
    {
      key: 'media', label: opts.mediaCount ? `Medien (${opts.mediaCount})` : 'Medien', icon: 'image', to: `/media?postIdeaId=${id}`,
      done: opts.mediaCount !== 0, title: opts.mediaCount === 0 ? 'Noch keine Medien verknüpft' : 'Medien dieser Idee',
    },
    ...(opts.flyerCount ? [{
      key: 'flyer' as const, label: `Flyer (${opts.flyerCount})`, icon: 'image' as const, to: `/flyers?postIdeaId=${id}`,
      done: true, title: 'Flyer zu dieser Idee',
    }] : []),
    { key: 'plan', label: 'Wochenplan', icon: 'calendar', to: `/editorial-plan?idea=${id}`, done: true, title: 'Slot im Wochenplan öffnen' },
  ];
  const omit = new Set(opts.omit ?? []);
  return links.filter((l) => !omit.has(l.key));
}
